import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import TesterDashboard from "./pages/TesterDashboard";
import ProductTypesPage from "./pages/ProductTypesPage";
import ProductsPage from "./pages/ProductsPage";
import StepsPage from "./pages/StepsPage";
import PhasesPage from "./pages/PhasesPage";
import ParametersPage from "./pages/ParametersPage";
import VerificationFlow from "./pages/VerificationFlow";
import TestReportsPage from "./pages/TestReportsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ComparisonPage from "./pages/ComparisonPage";
import NOCDashboard from "./pages/NOCDashboard";
import UserManagementPage from "./pages/UserManagementPage";
import FinetuningPage from "./pages/FinetuningPage";
import CustomQCReportPage from "./pages/CustomQCReportPage";
import HelpPage from "./pages/HelpPage";
import Layout from "./components/Layout";
import { CompressionProvider } from "./utils/CompressionContext";

// // Smart backend URL detection:
// // If the baked-in env URL points to a different domain than where we're running,
// // fall back to current origin. This ensures a single build works across
// // preview, production, and custom domains without CORS issues.
// const BACKEND_URL = (() => {
//   const envUrl = process.env.REACT_APP_BACKEND_URL;
//   if (!envUrl) return window.location.origin;
//   try {
//     if (new URL(envUrl).origin !== window.location.origin) {
//       return window.location.origin;
//     }
//     return envUrl;
//   } catch {
//     return window.location.origin;
//   }
// })();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send httpOnly cookies with every request
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (err) {
        if (err?.response?.status !== 401) {
          console.error("Auth init failed:", err?.message);
        }
        setUser(null);
        setToken(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.error("Logout request failed:", err?.message);
    }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "superadmin" || user.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/tester" replace />;
  }

  return children;
};

// Compression settings wrapper - reads token from auth context
const CompressionWrapper = ({ children }) => {
  const { token } = useAuth();
  return <CompressionProvider token={token}>{children}</CompressionProvider>;
};

// Route role constants – avoids recreating arrays on every render
const ROLES_ADMIN = ["admin", "superadmin"];
const ROLES_SUPERADMIN = ["superadmin"];
const ROLES_ALL = ["admin", "superadmin", "quality_engineer", "tester"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <CompressionWrapper>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><AdminDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/product-types" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><ProductTypesPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/products" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><ProductsPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/steps" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><StepsPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/phases" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><PhasesPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/parameters" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><ParametersPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><TestReportsPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><AnalyticsPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/comparison" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><ComparisonPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/noc" element={
            <ProtectedRoute allowedRoles={ROLES_SUPERADMIN}>
              <Layout><NOCDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><UserManagementPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/finetuning" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><FinetuningPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/custom-qc-report/:testId" element={
            <ProtectedRoute allowedRoles={ROLES_ADMIN}>
              <Layout><CustomQCReportPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Tester Routes */}
          <Route path="/tester" element={
            <ProtectedRoute allowedRoles={["quality_engineer", "tester"]}>
              <Layout><TesterDashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/tester/verify/:testId" element={
            <ProtectedRoute allowedRoles={ROLES_ALL}>
              <Layout><VerificationFlow /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/tester/reports" element={
            <ProtectedRoute allowedRoles={["quality_engineer", "tester"]}>
              <Layout><TestReportsPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/tester/custom-qc-report/:testId" element={
            <ProtectedRoute allowedRoles={ROLES_ALL}>
              <Layout><CustomQCReportPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Help Page - accessible by all authenticated users */}
          <Route path="/help" element={
            <ProtectedRoute allowedRoles={ROLES_ALL}>
              <Layout><HelpPage /></Layout>
            </ProtectedRoute>
          } />
          
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </CompressionWrapper>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
